import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';

import { useSearchQueryValue } from './useDebouncedSearch';

export type SearchQueryParams = {
  page: number;
  field?: string;
  search: string;
};

// eslint-disable-next-line no-unused-vars
type SearchQueryFn = (params: SearchQueryParams) => void;

type UseSearchQueryEffectOptions = {
  search: string;
  field?: string;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  query: SearchQueryFn;
  debounceMs?: number;
};

/**
 * Debounces search text, resets to page 1 when search/field changes, and keeps
 * pagination fetches separate to avoid duplicate requests.
 */
export function useSearchQueryEffect({
  search,
  field,
  currentPage,
  setCurrentPage,
  query,
  debounceMs,
}: UseSearchQueryEffectOptions): string {
  const debouncedSearch = useSearchQueryValue(search, debounceMs);
  const skipPaginationRef = useRef(false);

  useEffect(() => {
    skipPaginationRef.current = true;
    query({ page: 1, field, search: debouncedSearch });
    setCurrentPage(1);
  }, [debouncedSearch, field, query, setCurrentPage]);

  useEffect(() => {
    if (skipPaginationRef.current) {
      skipPaginationRef.current = false;
      return;
    }
    query({ page: currentPage, field, search: debouncedSearch });
  }, [currentPage, debouncedSearch, field, query]);

  return debouncedSearch;
}
